import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';

import { ThemeService } from '../../../../core/services/theme.service';
import { DashboardService, DashboardStats } from '../../service/dashboard.service';
import { EtablissementResponseDTO } from '../../../../models/EtablissementResponseDTO';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Component({
  selector: 'app-dashboards-admin',
  templateUrl: './dashboards-admin.component.html',
  styleUrls: ['./dashboards-admin.component.css'],
})
export class DashboardsAdminComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ─── Etablissement branding ──────────────────────────────────────────────
  etablissement: EtablissementResponseDTO | null = null;
  logoUrl = 'assets/img/monkey.png';
  etablissementNom = '';
  isLoading = true;
  today = '';

  // ─── KPI stats ───────────────────────────────────────────────────────────
  stats: DashboardStats = {
    totalParents: 0,
    totalEducateurs: 0,
    totalEleves: 0,
    totalClasses: 0,
    totalEmployees: 0,
    elevesByClass: [],
    parentsByMonth: [],
  };

  // ─── Colours from theme ──────────────────────────────────────────────────
  primaryColor = '#6c63ff';
  secondaryColor = '#a78bfa';
  editColor = '#3b82f6';
  exitColor = '#ef4444';
  submitColor = '#10b981';
  titleColor = '#1e1b4b';
  bgColor = '#f6f9fc';

  // BUG FIX: Selected class stats for bar chart click
  selectedClassStats: { label: string; count: number; index: number } | null = null;
  classDetails: { label: string; count: number }[] = [];

  // ─── Bar chart – Pupils per class ────────────────────────────────────────
  barChartType: ChartType = 'bar';
  barChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: '',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };
  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { stepSize: 1 } },
    },
    // BUG FIX: Enable click events on bars
    onClick: (event: any, elements: any[]) => {
      if (elements && elements.length > 0) {
        const idx = elements[0].index;
        this.onBarClick(idx);
      }
    },
  };

  // ─── Line chart – Parents per month ─────────────────────────────────────
  lineChartType: ChartType = 'line';
  lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: '',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
      },
    ],
  };
  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { stepSize: 1 } },
    },
  };

  // ─── Doughnut chart – People overview ───────────────────────────────────
  doughnutChartType: ChartType = 'doughnut';
  doughnutChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [{ data: [0, 0, 0], borderWidth: 3, hoverOffset: 8 }],
  };
  doughnutChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { usePointStyle: true, padding: 16, font: { size: 12, family: 'Inter, sans-serif' } },
      },
    },
  };

  constructor(
    public themeService: ThemeService,
    private dashboardService: DashboardService,
    private translate: TranslateService,
    private notification: NzNotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Set today date with current language locale
    this.updateTodayDate();

    // Re-render date & charts on language change
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateTodayDate();
        if (!this.isLoading) {
          this.buildCharts();
        }
      });

    // Subscribe to theme / etablissement data
    this.themeService.etablissement$
      .pipe(takeUntil(this.destroy$))
      .subscribe((etab) => {
        if (etab) {
          this.etablissement = etab;
          this.etablissementNom = etab.nom;
          this.logoUrl = this.themeService.buildLogoUrl(etab.logoUrl);
          this.extractColors(etab);
          this.refreshChartColors();
        }
      });

    // Load theme from backend (fallback to defaults on failure)
    this.themeService.loadAndApplyTheme().pipe(takeUntil(this.destroy$)).subscribe();

    // Load dashboard stats
    this.loadDashboardStats();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Date locale helpers ─────────────────────────────────────────────────

  private getDateLocale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'fr';
    const localeMap: { [key: string]: string } = {
      fr: 'fr-FR',
      ar: 'ar-TN',
      en: 'en-US',
    };
    return localeMap[lang] || 'fr-FR';
  }

  private updateTodayDate(): void {
    const locale = this.getDateLocale();
    this.today = new Date().toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // ─── Theme helpers ───────────────────────────────────────────────────────

  private extractColors(etab: EtablissementResponseDTO): void {
    this.primaryColor = etab.primaryColor || '#6c63ff';
    this.secondaryColor = etab.secodaryColor || '#a78bfa';
    this.editColor = etab.beditColor || '#3b82f6';
    this.exitColor = etab.bexitColor || '#ef4444';
    this.submitColor = etab.bsubmitColor || '#10b981';
    this.titleColor = etab.titleColor || '#1e1b4b';
    this.bgColor = etab.bgColor || '#f6f9fc';
  }

  // ─── Data loading ────────────────────────────────────────────────────────

  private loadDashboardStats(): void {
    this.isLoading = true;
    this.dashboardService
      .loadStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (s) => {
          this.stats = s;
          this.buildCharts();
          this.isLoading = false;
        },
        error: () => {
          this.notification.error('Erreur', 'Impossible de charger les statistiques du tableau de bord.');
          this.isLoading = false;
        },
      });
  }

  // ─── Chart builders ──────────────────────────────────────────────────────

  private buildCharts(): void {
    const t = (key: string) => this.translate.instant(key);

    // Bar – pupils per class
    this.barChartData = {
      labels: this.stats.elevesByClass.map((e) => e.label),
      datasets: [
        {
          data: this.stats.elevesByClass.map((e) => e.count),
          label: t('ADMIN_DASHBOARD.ELEVES_PAR_CLASSE'),
          backgroundColor: this.hexToRgba(this.primaryColor, 0.75),
          borderColor: this.primaryColor,
          borderWidth: 2,
          borderRadius: 8,
          borderSkipped: false,
          hoverBackgroundColor: this.primaryColor,
        },
      ],
    };

    // Line – parents per month
    this.lineChartData = {
      labels: this.stats.parentsByMonth.map((p) => p.month),
      datasets: [
        {
          data: this.stats.parentsByMonth.map((p) => p.count),
          label: t('ADMIN_DASHBOARD.CHART.NEW_PARENTS'),
          fill: true,
          tension: 0.4,
          borderColor: this.secondaryColor,
          backgroundColor: this.hexToRgba(this.secondaryColor, 0.12),
          pointBackgroundColor: this.secondaryColor,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 8,
        },
      ],
    };

    // Doughnut – people split
    this.doughnutChartData = {
      labels: [
        t('ADMIN_DASHBOARD.KPI.PARENTS'),
        t('ADMIN_DASHBOARD.KPI.EDUCATORS'),
        t('ADMIN_DASHBOARD.KPI.EMPLOYEES'),
      ],
      datasets: [
        {
          data: [this.stats.totalParents, this.stats.totalEducateurs, this.stats.totalEmployees],
          backgroundColor: [this.primaryColor, this.secondaryColor, this.editColor],
          borderColor: ['#fff', '#fff', '#fff'],
          borderWidth: 3,
          hoverOffset: 8,
        },
      ],
    };
  }

  private refreshChartColors(): void {
    if (this.stats.totalParents > 0 || this.stats.totalEducateurs > 0) {
      this.buildCharts();
    }
  }

  // ─── Public helpers ──────────────────────────────────────────────────────

  /** Convert hex color to rgba string */
  hexToRgba(hex: string, alpha: number): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return `rgba(108,99,255,${alpha})`;
    return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},${alpha})`;
  }

  /** KPI card config (translated) */
  get kpiCards() {
    return [
      {
        titleKey: 'ADMIN_DASHBOARD.KPI.PARENTS',
        value: this.stats.totalParents,
        icon: '👨‍👩‍👧',
        color: this.primaryColor,
        bg: this.hexToRgba(this.primaryColor, 0.1),
        trendKey: 'ADMIN_DASHBOARD.KPI.PARENTS_TREND',
      },
      {
        titleKey: 'ADMIN_DASHBOARD.KPI.EDUCATORS',
        value: this.stats.totalEducateurs,
        icon: '👩‍🏫',
        color: this.secondaryColor,
        bg: this.hexToRgba(this.secondaryColor, 0.1),
        trendKey: 'ADMIN_DASHBOARD.KPI.EDUCATORS_TREND',
      },
      {
        titleKey: 'ADMIN_DASHBOARD.KPI.STUDENTS',
        value: this.stats.totalEleves,
        icon: '🧒',
        color: this.editColor,
        bg: this.hexToRgba(this.editColor, 0.1),
        trendKey: 'ADMIN_DASHBOARD.KPI.STUDENTS_TREND',
      },
      {
        titleKey: 'ADMIN_DASHBOARD.KPI.CLASSES',
        value: this.stats.totalClasses,
        icon: '🏫',
        color: this.submitColor,
        bg: this.hexToRgba(this.submitColor, 0.1),
        trendKey: 'ADMIN_DASHBOARD.KPI.CLASSES_TREND',
      },
      {
        titleKey: 'ADMIN_DASHBOARD.KPI.EMPLOYEES',
        value: this.stats.totalEmployees,
        icon: '👷',
        color: this.exitColor,
        bg: this.hexToRgba(this.exitColor, 0.1),
        trendKey: 'ADMIN_DASHBOARD.KPI.EMPLOYEES_TREND',
      },
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }

  getBarWidth(value: number): number {
    const max = Math.max(
      1,
      this.stats.totalParents,
      this.stats.totalEducateurs,
      this.stats.totalEleves,
      this.stats.totalClasses,
      this.stats.totalEmployees
    );
    return Math.min(100, Math.max(0, (value / max) * 100));
  }

  // BUG FIX: Handle bar chart click – show statistics for the selected class
  onBarClick(index: number): void {
    if (index >= 0 && index < this.stats.elevesByClass.length) {
      const classData = this.stats.elevesByClass[index];
      this.selectedClassStats = {
        label: classData.label,
        count: classData.count,
        index,
      };
    }
  }

  clearClassSelection(): void {
    this.selectedClassStats = null;
  }
}
